import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { IMAGES } from "../lib/content";

const lineVariants = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 1, delay: 0.25 + i * 0.15, ease: [0.22, 1, 0.36, 1] },
  }),
};

const HEAD_LINES = ["Where Vision", "Becomes Value."];

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section id="home" ref={ref} data-testid="hero-section" className="relative h-[100svh] min-h-[640px] overflow-hidden noise-overlay">
      <motion.div style={{ y, scale }} className="absolute inset-0 z-0">
        <img src={IMAGES.hero} alt="Abstract corporate architecture representing KRV Nexus" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-navy/75" />
        <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/30 to-navy/60" />
      </motion.div>

      <motion.div style={{ opacity }} className="relative z-10 h-full max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 flex flex-col justify-center">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.15 }}
          className="flex items-center gap-3 mb-8"
        >
          <span className="h-px w-10 bg-gold" />
          <span className="text-xs sm:text-sm uppercase tracking-[0.3em] text-gold">KRV Nexus Pvt Ltd</span>
        </motion.div>

        <h1 className="font-serif font-light text-5xl sm:text-7xl lg:text-8xl leading-[0.95] tracking-tighter text-ivory max-w-5xl">
          {HEAD_LINES.map((line, i) => (
            <span key={line} className="block overflow-hidden pb-2">
              <motion.span custom={i} variants={lineVariants} initial="hidden" animate="show" className="block">
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.9 }}
          className="mt-8 max-w-2xl text-lg lg:text-xl text-ivory/70 leading-relaxed"
        >
          KRV Nexus is building a connected portfolio of businesses focused on creating meaningful value through retail, financial education, research, and future-ready ventures.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.1, duration: 0.9 }}
          className="mt-10 flex flex-col sm:flex-row gap-4"
        >
          <a href="#businesses" data-testid="hero-primary-cta" className="inline-flex items-center justify-center bg-gold text-navy px-8 py-4 rounded-full font-medium hover:bg-gold-muted transition-colors">
            Explore Our Businesses
          </a>
          <a href="#about" data-testid="hero-secondary-cta" className="inline-flex items-center justify-center border border-white/25 text-ivory px-8 py-4 rounded-full font-medium hover:border-gold hover:text-gold transition-colors">
            Discover KRV Nexus
          </a>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2 text-ivory/50"
      >
        <span className="text-[10px] uppercase tracking-[0.25em]">Scroll</span>
        <motion.span animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 1.8 }}>
          <ArrowDown size={16} />
        </motion.span>
      </motion.div>
    </section>
  );
};
